
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import { cn } from '@/lib/utils';
import { createSafeHTML } from '@/utils/sanitization';

interface PractitionerAboutProps {
  bio?: string | null;
  bioEn?: string | null;
  bioZh?: string | null;
}

const PractitionerAbout: React.FC<PractitionerAboutProps> = ({ bio, bioEn, bioZh }) => {
  const { t, i18n } = useTranslation('practitioners');
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const displayBio = getBilingualValue(i18n.language, bioEn, bioZh, bio);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight + 2);
    }
  }, [displayBio]);

  if (!displayBio) return null;

  const isHtml = /<[a-z][\s\S]*>/i.test(displayBio);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('about')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {isHtml ? (
            <div
              ref={contentRef}
              className={cn(
                'prose prose-sm max-w-none text-earth-700 leading-relaxed',
                !expanded && 'line-clamp-4'
              )}
              dangerouslySetInnerHTML={createSafeHTML(displayBio)}
            />
          ) : (
            <p
              ref={contentRef as React.RefObject<HTMLParagraphElement>}
              className={cn(
                'text-earth-700 leading-relaxed whitespace-pre-wrap',
                !expanded && 'line-clamp-4'
              )}
            >
              {displayBio}
            </p>
          )}
          {(isClamped || expanded) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-primary hover:underline mt-1 float-right"
            >
              {expanded ? t('showLess', 'Show less') : t('showMore', 'Show more')}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PractitionerAbout;
